import type { ReactNode } from 'react';
import { Skeleton, SkeletonCircle } from '@doccareplus/ui';

/**
 * Loading placeholders for the settings feature (clinic settings + account
 * settings). Each is a `role="status"` region (with an sr-only label)
 * wrapping `aria-hidden` shimmer primitives, so assistive tech announces
 * "loading" once instead of reading every block. Shapes mirror the real
 * layouts to avoid a jump when data arrives.
 */

function LoadingRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A bordered card placeholder: header label + a few label/field-shaped rows. */
function SectionCardSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div
      className="overflow-hidden rounded-[14px] border border-border bg-surface shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <div className="border-b border-border px-4 py-3">
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="grid gap-4 p-4 sm:grid-cols-2">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton className="h-3 w-1/3" />
            <Skeleton className="h-9 w-full rounded-[9px]" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Clinic Settings workspace — left sub-nav rail of pill-shaped section
 * placeholders plus a couple of section-shaped cards in the content pane.
 */
export function ClinicSettingsPageSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-2 pb-7" aria-hidden>
        <Skeleton className="h-6 w-52" />
        <Skeleton className="h-3.5 w-80" />
      </div>
      <div className="grid items-start gap-7 lg:grid-cols-[minmax(0,220px)_minmax(0,1fr)]" aria-hidden>
        <div className="scroll-hide flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:gap-2 lg:overflow-visible lg:pb-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-28 shrink-0 rounded-full lg:w-full" />
          ))}
        </div>
        <div className="flex flex-col gap-4">
          <SectionCardSkeleton rows={2} />
          <SectionCardSkeleton rows={4} />
        </div>
      </div>
    </LoadingRegion>
  );
}

/**
 * My Settings (account) page — page header, a two-column grid of
 * preferences/security cards, and a sessions section shaped like the real
 * device-session rows.
 */
export function AccountSettingsPageSkeleton({ label }: { label: string }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-4" aria-hidden>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-3.5 w-72" />
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          <SectionCardSkeleton rows={3} />
          <SectionCardSkeleton rows={2} />
        </div>
        <SessionsListSkeletonBody rows={3} />
      </div>
    </LoadingRegion>
  );
}

/** The stacked rows inside the sessions card: device icon + name/meta + revoke button. */
function SessionsListSkeletonBody({ rows }: { rows: number }) {
  return (
    <div
      className="overflow-hidden rounded-[14px] border border-border bg-surface shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <div className="border-b border-border px-4 py-3">
        <Skeleton className="h-4 w-40" />
      </div>
      <div className="flex flex-col p-1">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-3 last:border-b-0">
            <SkeletonCircle size={20} />
            <div className="min-w-[220px] flex-1 flex-col gap-2">
              <Skeleton className="h-3.5 w-2/5" />
              <Skeleton className="mt-2 h-3 w-3/5" />
            </div>
            <Skeleton className="h-8 w-20 rounded-[9px]" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** The sessions list only — used inline within the loaded account page while just the sessions query is pending. */
export function SessionsListSkeleton({ label, rows = 3 }: { label: string; rows?: number }) {
  return (
    <LoadingRegion label={label}>
      <SessionsListSkeletonBody rows={rows} />
    </LoadingRegion>
  );
}
